import { useState, useRef, useEffect, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'

const API = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3001'

function getToken(): string {
  return JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
}

// SSE event types from agent-aq.ts
interface AQEvent {
  type: string
  [key: string]: any
}

interface AQMessage {
  id: string
  events: AQEvent[]
  prompt: string
  status: 'running' | 'done' | 'error' | 'cancelled'
  runId?: string
}

export function AQRenderer() {
  const [messages, setMessages] = useState<AQMessage[]>([])
  const [input, setInput] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [currentRunId, setCurrentRunId] = useState<string | null>(null)
  const [pendingImage, setPendingImage] = useState<{ data: string; mimeType: string } | null>(null)
  const [projects, setProjects] = useState<string[]>([])
  const [activeProject, setActiveProject] = useState<string>('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Load project list on mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await fetch(`${API}/api/projects`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        })
        const data = await res.json()
        const slugs = (data.projects || data || []).map((p: any) => p.slug || p.name).filter(Boolean)
        setProjects(slugs)
        if (slugs.length > 0 && !activeProject) setActiveProject(slugs[0])
      } catch {}
    }
    loadProjects()
  }, [])

  // Load conversation when project changes
  useEffect(() => {
    if (!activeProject) return
    const loadConvo = async () => {
      try {
        const res = await fetch(`${API}/api/aq/conversation?project=${activeProject}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        })
        const data = await res.json()
        if (data.messages?.length > 0) {
          const loaded: AQMessage[] = []
          for (let i = 0; i < data.messages.length; i += 2) {
            const userMsg = data.messages[i]
            const assistantMsg = data.messages[i + 1]
            if (userMsg?.role === 'user') {
              // Parse assistant content to reconstruct events (terminals, text)
              const events: AQEvent[] = []
              if (assistantMsg?.content) {
                const content = assistantMsg.content
                // Remove [Read:] and [Modified:] prefixes
                const cleaned = content.replace(/^\[Read:.*?\]\n?/m, '').replace(/^\[Modified:.*?\]\n?/m, '').trim()
                
                // Split by terminal markers: lines starting with $ 
                const terminalRegex = /^\$ (.+?)\n([\s\S]*?)(?=^\$ |$(?!\n))/gm
                let lastIdx = 0
                let match: RegExpExecArray | null
                const tempCleaned = cleaned + '\n'
                
                while ((match = terminalRegex.exec(tempCleaned)) !== null) {
                  // Text before this terminal
                  const before = tempCleaned.slice(lastIdx, match.index).trim()
                  if (before) events.push({ type: 'text', text: before })
                  // Terminal command + output
                  events.push({ type: 'terminal', command: match[1] })
                  if (match[2]?.trim()) events.push({ type: 'terminal_output', output: match[2].trim() })
                  lastIdx = match.index + match[0].length
                }
                
                // Remaining text after last terminal
                const remaining = tempCleaned.slice(lastIdx).trim()
                if (remaining) {
                  // Check for summary line
                  const summaryMatch = remaining.match(/\[summary: read=(\d+), modified=(\d+), duration=([\d.]+)s\]/)
                  if (summaryMatch) {
                    const textWithoutSummary = remaining.replace(/\[summary:.*?\]/, '').trim()
                    if (textWithoutSummary) events.push({ type: 'text', text: textWithoutSummary })
                    events.push({ type: 'summary', filesRead: Array(+summaryMatch[1]).fill(''), filesModified: Array(+summaryMatch[2]).fill(''), durationMs: parseFloat(summaryMatch[3]) * 1000 })
                  } else {
                    events.push({ type: 'text', text: remaining })
                  }
                }
                
                // If no terminals found, just use as text
                if (events.length === 0 && cleaned) {
                  events.push({ type: 'text', text: cleaned })
                }
              }
              loaded.push({
                id: `hist-${i}`,
                prompt: userMsg.content,
                events,
                status: 'done',
              })
            }
          }
          setMessages(loaded)
        } else {
          setMessages([])
        }
      } catch {}
    }
    loadConvo()
  }, [activeProject])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendPrompt = useCallback(async () => {
    const prompt = input.trim()
    if (!prompt || isRunning) return
    setInput('')
    setIsRunning(true)

    const openTabs = useAppStore.getState().openTabs.map(t => t.path)
    const msgId = `msg-${Date.now()}`
    const isDevMode = prompt.startsWith('/dev ')
    const isTestMode = prompt.startsWith('/test ')
    const isDocsMode = prompt.startsWith('/docs ')
    const isReviewMode = prompt.startsWith('/review ')
    const isFixMode = prompt.startsWith('/fix ')
    const isScanMode = prompt.startsWith('/scan')
    const isTypeCheckMode = prompt.startsWith('/typecheck')
    const isShellMode = prompt.startsWith('$ ') || prompt.startsWith('> ')

    // Direct shell — still goes through agent stream so approval flow works
    if (isShellMode) {
      const command = prompt.slice(2).trim()
      const newMsg: AQMessage = { id: msgId, events: [], prompt, status: 'running' }
      setMessages(prev => [...prev, newMsg])

      const abort = new AbortController()
      abortRef.current = abort

      try {
        const res = await fetch(`${API}/api/aq/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ prompt: `run this terminal command: ${command}`, openTabs, project: activeProject }),
          signal: abort.signal,
        })
        const reader = res.body?.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (reader) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const event: AQEvent = JSON.parse(line.slice(6))
              if (event.type === 'ping') setCurrentRunId(event.runId)
              setMessages(prev => prev.map(m =>
                m.id === msgId ? { ...m, events: [...m.events, event] } : m
              ))
              if (event.type === 'done') setMessages(prev => prev.map(m => m.id === msgId ? { ...m, status: 'done' } : m))
              if (event.type === 'error') setMessages(prev => prev.map(m => m.id === msgId ? { ...m, status: 'error' } : m))
            } catch {}
          }
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          setMessages(prev => prev.map(m =>
            m.id === msgId ? { ...m, status: 'error', events: [...m.events, { type: 'error', message: e.message }] } : m
          ))
        }
      }
      setPendingImage(null)
      setIsRunning(false)
      setCurrentRunId(null)
      abortRef.current = null
      return
    }

    const actualPrompt = isDevMode ? prompt.slice(5).trim() : isTestMode ? `generate tests for ${prompt.slice(6).trim()}` : isDocsMode ? `generate docs for ${prompt.slice(6).trim()}` : isReviewMode ? `review code in ${prompt.slice(8).trim()}` : isFixMode ? `fix this error: ${prompt.slice(5).trim()}` : isScanMode ? `security scan ${prompt.slice(5).trim() || 'the project'}` : isTypeCheckMode ? `typecheck ${prompt.slice(10).trim() || 'the project'} and fix any errors` : prompt
    const newMsg: AQMessage = { id: msgId, events: [], prompt, status: 'running' }
    setMessages(prev => [...prev, newMsg])

    const abort = new AbortController()
    abortRef.current = abort

    const endpoint = isDevMode ? `${API}/api/aq/plan` : `${API}/api/aq/stream`

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ prompt: actualPrompt, openTabs, project: activeProject, ...(pendingImage && { image: pendingImage }) }),
        signal: abort.signal,
      })

      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (reader) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const event: AQEvent = JSON.parse(line.slice(6))
            if (event.type === 'ping') {
              setCurrentRunId(event.runId)
            }
            setMessages(prev => prev.map(m =>
              m.id === msgId ? { ...m, events: [...m.events, event] } : m
            ))
            if (event.type === 'done') {
              setMessages(prev => prev.map(m =>
                m.id === msgId ? { ...m, status: 'done' } : m
              ))
            }
            if (event.type === 'error') {
              setMessages(prev => prev.map(m =>
                m.id === msgId ? { ...m, status: 'error' } : m
              ))
            }
          } catch {}
        }
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        setMessages(prev => prev.map(m =>
          m.id === msgId ? { ...m, status: 'error', events: [...m.events, { type: 'error', message: e.message }] } : m
        ))
      }
    }
    setPendingImage(null)
    setIsRunning(false)
    setCurrentRunId(null)
    abortRef.current = null
  }, [input, isRunning, pendingImage])

  const handleCancel = async () => {
    abortRef.current?.abort()
    if (currentRunId) {
      fetch(`${API}/api/aq/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ runId: currentRunId }),
      }).catch(() => {})
    }
    setIsRunning(false)
  }

  const handleUndo = async () => {
    const res = await fetch(`${API}/api/aq/undo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    })
    const data = await res.json()
    if (data.ok) {
      setMessages(prev => [...prev, {
        id: `undo-${Date.now()}`, prompt: '', status: 'done',
        events: [{ type: 'text', text: `Undone: ${data.restored.join(', ')}` }],
      }])
    }
  }

  const handleApprovePlan = async () => {
    setIsRunning(true)
    const msgId = `exec-${Date.now()}`
    const newMsg: AQMessage = { id: msgId, events: [], prompt: 'Executing plan...', status: 'running' }
    setMessages(prev => [...prev, newMsg])

    try {
      const res = await fetch(`${API}/api/aq/execute-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      })
      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (reader) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const event: AQEvent = JSON.parse(line.slice(6))
            setMessages(prev => prev.map(m =>
              m.id === msgId ? { ...m, events: [...m.events, event] } : m
            ))
            if (event.type === 'done') setMessages(prev => prev.map(m => m.id === msgId ? { ...m, status: 'done' } : m))
          } catch {}
        }
      }
    } catch {}
    setIsRunning(false)
  }

  const handleRejectPlan = async () => {
    await fetch(`${API}/api/aq/reject-plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    }).catch(() => {})
    setMessages(prev => [...prev, {
      id: `reject-${Date.now()}`, prompt: '', status: 'done',
      events: [{ type: 'text', text: 'Plan rejected.' }],
    }])
  }

  const handleClear = async () => {
    setMessages([])
    fetch(`${API}/api/aq/clear`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ project: activeProject }),
    }).catch(() => {})
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0f1119', color: '#e0e0e0' }}>
      <style>{`@keyframes pulse-blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #2a2a3a', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.5px' }}>AQ Agent</span>
        {projects.length > 0 && (
          <select
            value={activeProject}
            onChange={e => setActiveProject(e.target.value)}
            style={{ fontSize: 11, background: '#1a1a2e', border: '1px solid #333', borderRadius: 3, padding: '2px 6px', color: '#ccc', outline: 'none', maxWidth: 120, fontFamily: 'monospace' }}
          >
            {projects.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        <span style={{ fontSize: 11, fontFamily: 'monospace', marginLeft: 'auto', color: isRunning ? '#0972d3' : '#666' }}>{isRunning ? 'running' : 'ready'}</span>
        {messages.length > 0 && <button onClick={handleClear} style={btnSmall}>clear</button>}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 10px' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', opacity: 0.5, marginTop: 40, fontSize: 13 }}>
            Ask me anything about your project.<br />I can read, edit, search, and run commands.
          </div>
        )}
        {messages.map(msg => <MessageBlock key={msg.id} msg={msg} onUndo={handleUndo} onApprovePlan={handleApprovePlan} onRejectPlan={handleRejectPlan} />)}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '8px 10px', borderTop: '1px solid #2a2a3a' }}>
        {/* Slash command suggestions */}
        {!isRunning && !input && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
            {['/dev', '/test', '/docs', '/review', '/fix', '/scan', '/typecheck'].map(cmd => (
              <button key={cmd} onClick={() => setInput(cmd + ' ')} style={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 12, padding: '2px 8px', fontSize: 10, color: '#888', cursor: 'pointer', fontFamily: 'monospace' }}>{cmd}</button>
            ))}
          </div>
        )}
        {pendingImage && (
          <div style={{ fontSize: 11, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'monospace', color: '#888' }}>
            image attached <button onClick={() => setPendingImage(null)} style={{ ...btnSmall, fontSize: 10, padding: '1px 5px' }}>x</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendPrompt() } }}
            onPaste={e => {
              const items = e.clipboardData?.items
              if (!items) return
              for (const item of items) {
                if (item.type.startsWith('image/')) {
                  const file = item.getAsFile()
                  if (!file) continue
                  const reader = new FileReader()
                  reader.onload = () => {
                    const base64 = (reader.result as string).split(',')[1]
                    setPendingImage({ data: base64, mimeType: item.type })
                  }
                  reader.readAsDataURL(file)
                  e.preventDefault()
                  break
                }
              }
            }}
            placeholder={pendingImage ? 'Describe what to do with this image...' : 'Ask AQ agent... (Shift+Enter for newline)'}
            disabled={isRunning}
            rows={1}
            style={{ flex: 1, background: '#1a1a2e', border: '1px solid #333', borderRadius: 4, padding: '6px 10px', color: '#e0e0e0', fontSize: 13, outline: 'none', fontFamily: 'monospace', resize: 'none', minHeight: 32, maxHeight: 120, overflow: 'auto' }}
          />
          {isRunning
            ? <button onClick={handleCancel} style={{ ...btnPrimary, background: '#e53e3e' }}>Stop</button>
            : <button onClick={sendPrompt} disabled={!input.trim()} style={btnPrimary}>Send</button>
          }
        </div>
      </div>
    </div>
  )
}

// ── Message Block ────────────────────────────────────────────

function MessageBlock({ msg, onUndo, onApprovePlan, onRejectPlan }: { msg: AQMessage; onUndo: () => void; onApprovePlan: () => void; onRejectPlan: () => void }) {
  const textParts: string[] = []
  const steps: AQEvent[] = []
  let summary: AQEvent | null = null
  let plan: AQEvent | null = null
  let pendingApproval: AQEvent | null = null
  const terminalCommands: Array<{ command: string; output: string }> = []

  let currentCmd = ''
  let currentOutput = ''

  for (const ev of msg.events) {
    if (ev.type === 'text') textParts.push(ev.text)
    else if (ev.type === 'summary') summary = ev
    else if (ev.type === 'plan') plan = ev
    else if (ev.type === 'approval_required') pendingApproval = ev
    else if (ev.type === 'terminal') {
      if (currentCmd) terminalCommands.push({ command: currentCmd, output: currentOutput })
      currentCmd = ev.command
      currentOutput = ''
    }
    else if (ev.type === 'terminal_output') {
      currentOutput += ev.output
      // Mirror to real terminal
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('pulse:aq-terminal-output', { detail: { output: ev.output } }))
      }
    }
    else if (ev.type !== 'ping' && ev.type !== 'done') steps.push(ev)
  }
  if (currentCmd) terminalCommands.push({ command: currentCmd, output: currentOutput })

  // Clear pending approval if action was already executed (subsequent events exist after it)
  if (pendingApproval) {
    const approvalIdx = msg.events.indexOf(pendingApproval)
    const hasSubsequentAction = msg.events.slice(approvalIdx + 1).some(e => 
      e.type === 'file_edit' || e.type === 'file_write' || e.type === 'file_delete' || e.type === 'terminal' || e.type === 'thinking'
    )
    if (hasSubsequentAction || msg.status === 'done') pendingApproval = null
  }

  const text = textParts.join('')

  return (
    <div style={{ marginBottom: 14 }}>
      {/* User prompt */}
      {msg.prompt && (
        <div style={{ background: '#1a1a2e', borderRadius: 4, padding: '6px 10px', marginBottom: 6, fontSize: 13, fontFamily: 'monospace', borderLeft: '2px solid #0972d3' }}>
          <span style={{ color: '#666', fontSize: 11 }}>you:</span> {msg.prompt}
        </div>
      )}

      {/* Steps (collapsible groups) */}
      <StepGroup steps={steps} />

      {/* Terminal commands */}
      {terminalCommands.map((tc, i) => {
        const isLastCmd = i === terminalCommands.length - 1
        return <AQTerminalCard key={`term-${i}`} command={tc.command} output={tc.output} isRunning={msg.status === 'running' && isLastCmd} />
      })}

      {/* Terminal approval (command not yet run) */}
      {pendingApproval && pendingApproval.tool === 'terminal_run' && msg.status === 'running' && terminalCommands.length === 0 && (
        <AQTerminalCard command={pendingApproval.args?.command || ''} output="" isRunning={false} needsApproval={true} runId={pendingApproval.runId} />
      )}

      {/* Pending approval card (non-terminal) */}
      {pendingApproval && pendingApproval.tool !== 'terminal_run' && msg.status === 'running' && (
        <ApprovalCard event={pendingApproval} runId={pendingApproval.runId} />
      )}

      {/* Response text (markdown rendered) */}
      {text && (
        <div style={{ fontSize: 13, lineHeight: 1.6, padding: '4px 0' }}>
          <MarkdownText text={text} />
          {msg.status === 'running' && <span style={{ opacity: 0.5, animation: 'pulse-blink 1s infinite' }}>▊</span>}
        </div>
      )}

      {/* Plan card */}
      {plan && <PlanCard event={plan} onApprove={onApprovePlan} onReject={onRejectPlan} />}

      {/* Summary card */}
      {summary && <SummaryCard event={summary} onUndo={onUndo} />}

      {/* Status indicator */}
      {msg.status === 'running' && (
        <div style={{ fontSize: 11, color: '#666', marginTop: 4, fontFamily: 'monospace' }}>working...</div>
      )}
    </div>
  )
}

// ── Clickable File Path ────────────────────────────────────

function FilePath({ path: filePath }: { path: string }) {
  const openFile = () => {
    // Dispatch event to open file in editor
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pulse:open-file', { detail: { path: filePath } }))
    }
  }

  return (
    <span
      onClick={openFile}
      style={{ color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#60a5fa44', fontWeight: 500 }}
      title={`Open ${filePath}`}
    >
      {filePath}
    </span>
  )
}

// ── Collapsible Thinking ──────────────────────────────────────

function CollapsibleThinking({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div style={{ margin: '2px 0' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ ...thinkingStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, userSelect: 'none' }}
      >
        <span style={{ fontSize: 10, color: '#555', transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        <span style={{ color: '#666' }}>thinking...</span>
      </div>
      {expanded && (
        <div style={{ marginLeft: 12, borderLeft: '1px solid #333', paddingLeft: 8, fontSize: 11, fontFamily: 'monospace', color: '#888', padding: '4px 8px' }}>
          {text}
        </div>
      )}
    </div>
  )
}

// ── Collapsible Step Group ───────────────────────────────────

function StepGroup({ steps }: { steps: AQEvent[] }) {
  // Group consecutive file_read events
  const groups: Array<{ type: 'reads' | 'single'; events: AQEvent[] }> = []
  let currentReads: AQEvent[] = []

  for (const ev of steps) {
    if (ev.type === 'file_read') {
      currentReads.push(ev)
    } else {
      if (currentReads.length > 0) {
        groups.push({ type: 'reads', events: [...currentReads] })
        currentReads = []
      }
      groups.push({ type: 'single', events: [ev] })
    }
  }
  if (currentReads.length > 0) {
    groups.push({ type: 'reads', events: currentReads })
  }

  return (
    <>
      {groups.map((group, gi) => {
        if (group.type === 'reads' && group.events.length > 1) {
          return <CollapsibleReads key={gi} events={group.events} />
        }
        return group.events.map((ev, ei) => <StepEvent key={`${gi}-${ei}`} event={ev} />)
      })}
    </>
  )
}

function CollapsibleReads({ events }: { events: AQEvent[] }) {
  const [expanded, setExpanded] = useState(false)
  const doneCount = events.filter(e => e.status === 'done').length
  const label = `Read ${doneCount} file${doneCount !== 1 ? 's' : ''}`

  return (
    <div style={{ margin: '2px 0' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ ...stepStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, userSelect: 'none' }}
      >
        <span style={{ fontSize: 10, color: '#666', transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        <span>{label}</span>
      </div>
      {expanded && (
        <div style={{ marginLeft: 12, borderLeft: '1px solid #333', paddingLeft: 8 }}>
          {events.map((ev, i) => (
            <div key={i} style={{ fontSize: 11, padding: '2px 0', fontFamily: 'monospace', color: '#888' }}>
              {ev.status === 'done' ? `${ev.path} (${ev.lines} lines)` : ev.status === 'reading' ? `${ev.path}...` : `${ev.path} not found`}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Step Event Renderer ──────────────────────────────────────

function StepEvent({ event }: { event: AQEvent }) {
  switch (event.type) {
    case 'thinking':
      return <CollapsibleThinking text={event.text} />

    case 'file_read':
      return (
        <div style={stepStyle}>
          {event.status === 'reading' ? <>read <FilePath path={event.path} />...</> : event.status === 'done' ? <>read <FilePath path={event.path} /> ({event.lines} lines)</> : <><FilePath path={event.path} /> not found</>}
        </div>
      )

    case 'file_edit':
      return <div style={stepStyle}>edit <FilePath path={event.path} /> ({event.lines} lines)</div>

    case 'file_write':
      return <div style={stepStyle}>create <FilePath path={event.path} /> ({event.lines} lines)</div>

    case 'file_delete':
      return <div style={stepStyle}>delete <FilePath path={event.path} /></div>

    case 'file_rename':
      return <div style={stepStyle}>rename <FilePath path={event.from} /> → <FilePath path={event.to} /></div>

    case 'search':
      return (
        <div style={stepStyle}>
          {event.results !== undefined ? `search "${event.query}" — ${event.results} matches` : `search "${event.query}"...`}
        </div>
      )

    case 'terminal':
      return null

    case 'terminal_output':
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('pulse:aq-terminal-output', { detail: { output: event.output } }))
      }
      return null

    case 'code_block':
      return (
        <div style={{ background: '#0d1117', borderRadius: 4, padding: '8px 10px', margin: '4px 0', overflow: 'auto', maxHeight: 200, border: '1px solid #2a2a3a' }}>
          {event.language && <div style={{ fontSize: 10, color: '#666', marginBottom: 4, fontFamily: 'monospace' }}>{event.language}</div>}
          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{event.code}</pre>
        </div>
      )

    case 'diff_preview':
      return (
        <div style={{ ...stepStyle, background: '#0d1117' }}>
          preview: <FilePath path={event.path} /> ({event.totalLines} lines)
          <pre style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.8, maxHeight: 80, overflow: 'auto' }}>{event.preview}</pre>
        </div>
      )

    case 'approval_required':
      return <div style={{ ...stepStyle, borderLeft: '2px solid #f59e0b' }}>approval needed: {event.tool} on {event.args?.path || event.args?.command}</div>

    case 'diff':
      return <DiffView path={event.path} oldContent={event.oldContent} newContent={event.newContent} />

    case 'clarification':
      return <div style={{ ...stepStyle, borderLeft: '2px solid #0972d3' }}>{event.question}</div>

    case 'error':
      return <div style={{ ...stepStyle, color: '#f87171', borderLeft: '2px solid #dc2626' }}>{event.message}</div>

    default:
      return null
  }
}

// ── Plan Card (aq_17) ───────────────────────────────────────

function PlanCard({ event, onApprove, onReject }: { event: AQEvent; onApprove: () => void; onReject: () => void }) {
  const steps = event.steps || []
  return (
    <div style={{ background: '#0d1117', border: '1px solid #0972d3', borderRadius: 4, padding: '10px 12px', marginTop: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, fontFamily: 'monospace' }}>Plan ({steps.length} step{steps.length !== 1 ? 's' : ''})</div>
      {steps.map((s: any, i: number) => (
        <div key={i} style={{ fontSize: 12, padding: '3px 0', display: 'flex', gap: 6, fontFamily: 'monospace' }}>
          <span style={{ color: '#666' }}>{i + 1}.</span>
          <span style={{ color: '#0972d3' }}>{s.action}</span>
          <span><b>{s.path}</b> — {s.description}</span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={onApprove} style={{ ...btnPrimary, background: '#16a34a', fontSize: 12, padding: '5px 14px' }}>Approve</button>
        <button onClick={onReject} style={{ ...btnSmall, color: '#f87171' }}>Reject</button>
      </div>
    </div>
  )
}

// ── Summary Card ─────────────────────────────────────────────

function SummaryCard({ event, onUndo }: { event: AQEvent; onUndo: () => void }) {
  return (
    <div style={{ background: '#0d1117', borderRadius: 4, padding: '8px 12px', marginTop: 6, fontSize: 12, fontFamily: 'monospace', borderLeft: '2px solid #16a34a' }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', color: '#aaa' }}>
        {event.filesRead?.length > 0 && <span>read: {event.filesRead.length}</span>}
        {event.filesModified?.length > 0 && <span>modified: {event.filesModified.length}</span>}
        {event.durationMs && <span>{(event.durationMs / 1000).toFixed(1)}s</span>}
      </div>
      {event.filesModified?.length > 0 && (
        <div style={{ marginTop: 4, color: '#666' }}>{event.filesModified.join(', ')}</div>
      )}
      {event.undoAvailable && (
        <button onClick={onUndo} style={{ ...btnSmall, marginTop: 6, color: '#f87171', borderColor: '#dc2626' }}>undo</button>
      )}
    </div>
  )
}

// ── Diff View (inline green/red) ─────────────────────────────

function DiffView({ path: filePath, oldContent, newContent }: { path: string; oldContent: string; newContent: string }) {
  const [expanded, setExpanded] = useState(false)
  const oldLines = oldContent.split('\n')
  const newLines = newContent.split('\n')

  // Simple line diff
  const diffs: Array<{ type: 'same' | 'add' | 'remove'; line: string; num: number }> = []
  const maxLen = Math.max(oldLines.length, newLines.length)
  for (let i = 0; i < maxLen; i++) {
    if (i < oldLines.length && i < newLines.length && oldLines[i] === newLines[i]) {
      diffs.push({ type: 'same', line: newLines[i], num: i + 1 })
    } else {
      if (i < oldLines.length) diffs.push({ type: 'remove', line: oldLines[i], num: i + 1 })
      if (i < newLines.length) diffs.push({ type: 'add', line: newLines[i], num: i + 1 })
    }
  }

  // Only show changed lines + 2 context lines
  const changedIndices = new Set<number>()
  diffs.forEach((d, i) => { if (d.type !== 'same') { for (let j = Math.max(0, i - 2); j <= Math.min(diffs.length - 1, i + 2); j++) changedIndices.add(j) } })
  const visible = expanded ? diffs : diffs.filter((_, i) => changedIndices.has(i))
  const changes = diffs.filter(d => d.type !== 'same').length

  if (changes === 0) return null

  return (
    <div style={{ background: '#0d1117', borderRadius: 4, margin: '4px 0', overflow: 'hidden', border: '1px solid #2a2a3a' }}>
      <div style={{ padding: '4px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid #2a2a3a', fontFamily: 'monospace' }}>
        <span>{filePath}</span>
        <span style={{ color: '#666' }}>({changes} change{changes !== 1 ? 's' : ''})</span>
        <button onClick={() => setExpanded(!expanded)} style={{ ...btnSmall, marginLeft: 'auto', fontSize: 10 }}>{expanded ? 'collapse' : 'expand'}</button>
      </div>
      <div style={{ maxHeight: 200, overflow: 'auto', fontFamily: 'monospace', fontSize: 11, lineHeight: 1.4 }}>
        {visible.map((d, i) => (
          <div key={i} style={{
            padding: '0 8px',
            background: d.type === 'add' ? '#1a3a1a' : d.type === 'remove' ? '#3a1a1a' : 'transparent',
            color: d.type === 'add' ? '#68d391' : d.type === 'remove' ? '#fc8181' : 'var(--text-primary)',
            opacity: d.type === 'same' ? 0.5 : 1,
          }}>
            <span style={{ opacity: 0.4, marginRight: 8, userSelect: 'none' }}>{d.type === 'add' ? '+' : d.type === 'remove' ? '-' : ' '}</span>
            {d.line || ' '}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Approval Card (blocking approval flow) ───────────────────

function ApprovalCard({ event, runId }: { event: AQEvent; runId?: string }) {
  const [responded, setResponded] = useState(false)

  const handleApprove = async () => {
    if (!runId || responded) return
    setResponded(true)
    await fetch(`${API}/api/aq/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ runId }),
    }).catch(() => {})
  }

  const handleReject = async () => {
    if (!runId || responded) return
    setResponded(true)
    await fetch(`${API}/api/aq/reject-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ runId }),
    }).catch(() => {})
  }

  return (
    <div style={{ background: '#1a1a0d', border: '1px solid #f59e0b', borderRadius: 4, padding: '8px 12px', margin: '4px 0' }}>
      <div style={{ fontSize: 12, marginBottom: 6, fontFamily: 'monospace' }}>
        <b>{event.tool}</b> wants to {event.tool === 'file_write' ? 'create' : event.tool === 'file_delete' ? 'delete' : event.tool === 'terminal_run' ? 'run' : 'execute'}: <code>{event.args?.path || event.args?.command || event.args?.packages?.join(', ') || ''}</code>
      </div>
      {!responded ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleApprove} style={{ ...btnPrimary, background: '#16a34a', fontSize: 11, padding: '4px 12px' }}>Approve</button>
          <button onClick={handleReject} style={{ ...btnSmall, color: '#f87171', fontSize: 11 }}>Reject</button>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: '#666', fontFamily: 'monospace' }}>responded</div>
      )}
    </div>
  )
}

// ── AQ Terminal Card ─────────────────────────────────────────

function AQTerminalCard({ command, output, isRunning, needsApproval, runId }: { command: string; output: string; isRunning: boolean; needsApproval?: boolean; runId?: string }) {
  const [approved, setApproved] = useState(!needsApproval)

  const handleApprove = async () => {
    if (!runId) return
    setApproved(true)
    await fetch(`${API}/api/aq/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ runId }),
    }).catch(() => {})
  }

  const handleReject = async () => {
    if (!runId) return
    setApproved(true)
    await fetch(`${API}/api/aq/reject-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ runId }),
    }).catch(() => {})
  }

  return (
    <div style={{ borderRadius: 4, overflow: 'hidden', margin: '4px 0', border: '1px solid #2a2a3a', background: '#0d1117' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', background: '#161825', borderBottom: '1px solid #2a2a3a', fontSize: 10, fontFamily: 'monospace' }}>
        <span style={{ color: '#16a34a' }}>shell</span>
        <button
          onClick={() => navigator.clipboard.writeText(`$ ${command}\n${output}`)}
          style={{ background: 'none', border: '1px solid #333', borderRadius: 3, color: '#888', cursor: 'pointer', fontSize: 9, padding: '1px 6px' }}
        >copy</button>
      </div>
      {/* Command */}
      <div style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 12, color: '#60a5fa' }}>
        <span style={{ color: '#16a34a' }}>$ </span>{command}
      </div>
      {/* Approval buttons inside terminal */}
      {needsApproval && !approved && (
        <div style={{ padding: '6px 10px', display: 'flex', gap: 8, borderTop: '1px solid #1e2030' }}>
          <button onClick={handleApprove} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 3, padding: '4px 12px', fontSize: 11, cursor: 'pointer' }}>Run</button>
          <button onClick={handleReject} style={{ background: 'transparent', border: '1px solid #f87171', borderRadius: 3, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: '#f87171' }}>Reject</button>
        </div>
      )}
      {/* Output */}
      {approved && (output || isRunning) && (
        <div style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11, color: '#ccc', whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto', borderTop: '1px solid #1e2030' }}>
          {output || (isRunning ? '' : '(no output)')}
          {isRunning && !output && <span style={{ opacity: 0.5, animation: 'pulse-blink 1s infinite' }}>█</span>}
        </div>
      )}
    </div>
  )
}

// ── Markdown Renderer ────────────────────────────────────────

function MarkdownText({ text }: { text: string }) {
  const parts = text.split(/(```[\s\S]*?```|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|^#{1,3}\s.+$|^- .+$|^\d+\. .+$|\[([^\]]+)\]\(([^)]+)\))/gm)

  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null

        // Fenced code block
        if (part.startsWith('```') && part.endsWith('```')) {
          const firstNewline = part.indexOf('\n')
          const lang = part.slice(3, firstNewline > 0 ? firstNewline : 3).trim()
          const code = firstNewline > 0 ? part.slice(firstNewline + 1, -3) : part.slice(3, -3)
          return <CodeBlock key={i} code={code} language={lang} />
        }

        // Inline code
        if (part.startsWith('`') && part.endsWith('`') && !part.startsWith('``')) {
          return <code key={i} style={{ background: '#1a1a2e', padding: '1px 5px', borderRadius: 3, fontSize: 12, fontFamily: 'monospace', color: '#7dd3fc' }}>{part.slice(1, -1)}</code>
        }

        // Bold
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>
        }

        // Italic
        if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
          return <em key={i}>{part.slice(1, -1)}</em>
        }

        // Headers
        if (/^#{1,3}\s/.test(part)) {
          const level = part.match(/^(#+)/)?.[1].length || 1
          const content = part.replace(/^#+\s/, '')
          const sizes: Record<number, number> = { 1: 16, 2: 14, 3: 13 }
          return <div key={i} style={{ fontSize: sizes[level] || 13, fontWeight: 600, margin: '8px 0 4px' }}>{content}</div>
        }

        // Link [text](url)
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
        if (linkMatch) {
          return <a key={i} href={linkMatch[2]} target="_blank" rel="noopener" style={{ color: '#60a5fa', textDecoration: 'underline' }}>{linkMatch[1]}</a>
        }

        // List items
        if (part.startsWith('- ')) {
          return <div key={i} style={{ paddingLeft: 12, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: '#666' }}>•</span>{part.slice(2)}</div>
        }
        if (/^\d+\.\s/.test(part)) {
          const num = part.match(/^(\d+)\.\s/)?.[1]
          const content = part.replace(/^\d+\.\s/, '')
          return <div key={i} style={{ paddingLeft: 16, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: '#666' }}>{num}.</span>{content}</div>
        }

        // Plain text — preserve newlines
        return <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>
      })}
    </>
  )
}

// ── Code Block with Copy + Insert + Syntax Highlighting ─────

const TOKEN_COLORS: Record<string, string> = {
  keyword: '#c678dd',
  string: '#98c379',
  number: '#d19a66',
  comment: '#5c6370',
  function: '#61afef',
  type: '#e5c07b',
  operator: '#56b6c2',
  punctuation: '#abb2bf',
}

function highlightCode(code: string, language: string): Array<{ text: string; color?: string }> {
  const tokens: Array<{ text: string; color?: string }> = []
  const keywords = /\b(const|let|var|function|return|if|else|for|while|import|export|from|class|interface|type|async|await|new|this|try|catch|throw|switch|case|break|default|extends|implements|static|public|private|protected|void|null|undefined|true|false|typeof|instanceof)\b/g
  const strings = /(['"`])(?:(?!\1|\\).|\\.)*\1/g
  const numbers = /\b\d+(\.\d+)?\b/g
  const comments = /\/\/.*$|\/\*[\s\S]*?\*\//gm
  const functions = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?=\()/g
  const types = /\b[A-Z][a-zA-Z0-9]*\b/g

  // Simple tokenizer: split by patterns and colorize
  let remaining = code
  let lastIndex = 0
  const matches: Array<{ start: number; end: number; color: string }> = []

  const addMatches = (regex: RegExp, color: string) => {
    let m: RegExpExecArray | null
    const r = new RegExp(regex.source, regex.flags)
    while ((m = r.exec(code)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length, color })
    }
  }

  addMatches(comments, TOKEN_COLORS.comment)
  addMatches(strings, TOKEN_COLORS.string)
  addMatches(keywords, TOKEN_COLORS.keyword)
  addMatches(numbers, TOKEN_COLORS.number)
  addMatches(functions, TOKEN_COLORS.function)
  addMatches(types, TOKEN_COLORS.type)

  // Sort by start position, prioritize comments > strings > keywords
  matches.sort((a, b) => a.start - b.start || b.end - a.end)

  // Remove overlapping matches (first match wins)
  const filtered: typeof matches = []
  let maxEnd = 0
  for (const m of matches) {
    if (m.start >= maxEnd) {
      filtered.push(m)
      maxEnd = m.end
    }
  }

  // Build token array
  let pos = 0
  for (const m of filtered) {
    if (m.start > pos) {
      tokens.push({ text: code.slice(pos, m.start) })
    }
    tokens.push({ text: code.slice(m.start, m.end), color: m.color })
    pos = m.end
  }
  if (pos < code.length) {
    tokens.push({ text: code.slice(pos) })
  }

  return tokens.length > 0 ? tokens : [{ text: code }]
}

function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false)
  const tokens = highlightCode(code, language)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const handleInsert = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pulse:insert-code', { detail: { code, language } }))
    }
  }

  return (
    <div style={{ background: '#0d1117', borderRadius: 4, margin: '6px 0', overflow: 'hidden', border: '1px solid #2a2a3a' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', borderBottom: '1px solid #2a2a3a', background: '#161825' }}>
        <span style={{ fontSize: 10, color: '#666', fontFamily: 'monospace' }}>{language || 'code'}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={handleInsert} style={{ background: 'none', border: '1px solid #333', borderRadius: 3, color: '#888', cursor: 'pointer', fontSize: 9, padding: '1px 6px' }}>insert</button>
          <button onClick={handleCopy} style={{ background: 'none', border: '1px solid #333', borderRadius: 3, color: copied ? '#16a34a' : '#888', cursor: 'pointer', fontSize: 9, padding: '1px 6px' }}>{copied ? 'copied' : 'copy'}</button>
        </div>
      </div>
      <div style={{ padding: '8px 10px', overflow: 'auto', maxHeight: 300 }}>
        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
          {tokens.map((t, i) => (
            <span key={i} style={t.color ? { color: t.color } : undefined}>{t.text}</span>
          ))}
        </pre>
      </div>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────

const stepStyle: React.CSSProperties = {
  fontSize: 12, padding: '4px 10px', margin: '2px 0', borderRadius: 3,
  background: '#1a1a2e', fontFamily: 'monospace', borderLeft: '2px solid #444',
}

const thinkingStyle: React.CSSProperties = {
  fontSize: 12, padding: '4px 10px', margin: '2px 0', borderRadius: 3,
  fontFamily: 'monospace', opacity: 0.7, color: '#888', borderLeft: '2px solid #555',
}

const btnPrimary: React.CSSProperties = {
  background: '#0972d3', color: '#fff', border: 'none',
  borderRadius: 4, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500,
}

const btnSmall: React.CSSProperties = {
  background: 'transparent', border: '1px solid #444', borderRadius: 3,
  padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#aaa',
}
